import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { ShieldCheck, AlertTriangle, CheckCircle2, Loader2, Search, BadgeCheck } from "lucide-react";

function transparency(vaga) {
  const checks = [
    Boolean(vaga.company_name),
    Boolean(vaga.cidade),
    Boolean(vaga.tipo),
    Boolean(vaga.descricao && vaga.descricao.trim().length >= 80),
    Boolean(vaga.salario && !/combinar|compatível|a definir/i.test(vaga.salario)),
    Boolean(vaga.whatsapp || vaga.email || vaga.url),
  ];
  const total = checks.filter(Boolean).length;
  return {
    total,
    label: total >= 5 ? "Alta transparência" : total >= 4 ? "Boa transparência" : "Confira os detalhes",
    tone: total >= 5 ? "emerald" : total >= 4 ? "blue" : "amber",
  };
}

export default function VagaLimpa() {
  const [vagas, setVagas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busca, setBusca] = useState("");

  useEffect(() => {
    api.get("/vagas")
      .then((r) => setVagas(r.data || []))
      .finally(() => setLoading(false));
  }, []);

  const filtradas = useMemo(() => {
    const q = busca.trim().toLowerCase();
    return vagas
      .map((vaga) => ({ ...vaga, transparency: transparency(vaga) }))
      .filter((vaga) => !q || `${vaga.cargo} ${vaga.company_name} ${vaga.cidade}`.toLowerCase().includes(q))
      .sort((a, b) => b.transparency.total - a.transparency.total);
  }, [vagas, busca]);

  return (
    <main className="max-w-7xl mx-auto px-5 py-10 md:py-14">
      <section className="rounded-3xl bg-slate-950 text-white p-7 md:p-10 overflow-hidden relative">
        <div className="absolute -right-20 -top-20 w-64 h-64 rounded-full bg-emerald-400/10" />
        <div className="relative max-w-3xl">
          <span className="inline-flex items-center gap-2 text-emerald-300 font-extrabold text-sm uppercase tracking-wider">
            <ShieldCheck size={18} /> VagaLimpa
          </span>
          <h1 className="font-heading text-4xl md:text-5xl font-extrabold tracking-tight mt-3">Mais clareza antes de se candidatar</h1>
          <p className="text-slate-300 text-lg mt-4 leading-relaxed">
            Organizamos os dados já publicados em cada vaga e destacamos anúncios com mais informações. O selo é um indicador de transparência do anúncio, não uma garantia sobre a empresa.
          </p>
        </div>
      </section>

      <section className="grid md:grid-cols-3 gap-4 mt-8">
        {[
          ["Salário informado", "Prefira anúncios que expliquem a remuneração ou faixa salarial."],
          ["Descrição completa", "Confira atividades, jornada, requisitos e forma de candidatura."],
          ["Nunca pague para concorrer", "Desconfie de cobrança para liberar entrevista, vaga ou contratação."],
        ].map(([title, text], i) => (
          <div key={title} className="bg-white border border-slate-200 rounded-2xl p-5 shadow-[0_8px_30px_rgba(0,0,0,0.04)]">
            {i === 2 ? <AlertTriangle className="text-amber-600" size={24} /> : <CheckCircle2 className="text-emerald-600" size={24} />}
            <h2 className="font-heading font-extrabold text-lg mt-3">{title}</h2>
            <p className="text-slate-600 mt-2 leading-relaxed">{text}</p>
          </div>
        ))}
      </section>

      <section className="mt-12">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-6">
          <div>
            <h2 className="font-heading text-3xl font-extrabold tracking-tight">Painel de transparência das vagas</h2>
            <p className="text-slate-600 mt-1">A pontuação usa somente informações visíveis no anúncio.</p>
          </div>
          <div className="relative md:w-80">
            <Search className="absolute left-3 top-3.5 text-slate-400" size={18} />
            <input value={busca} onChange={(e) => setBusca(e.target.value)} placeholder="Buscar cargo ou empresa" className="w-full bg-white border border-slate-200 rounded-xl py-3 pl-10 pr-4 outline-none focus:ring-2 focus:ring-blue-500" />
          </div>
        </div>

        {loading ? (
          <div className="py-20 flex justify-center text-slate-500"><Loader2 className="animate-spin" /></div>
        ) : (
          <div className="grid md:grid-cols-2 gap-5">
            {filtradas.map((vaga) => {
              const s = vaga.transparency;
              const badge = s.tone === "emerald" ? "bg-emerald-100 text-emerald-800" : s.tone === "blue" ? "bg-blue-100 text-blue-800" : "bg-amber-100 text-amber-800";
              return (
                <Link key={vaga.id} to={`/vaga/${vaga.id}`} className="bg-white border border-slate-200 rounded-2xl p-6 hover:-translate-y-1 hover:shadow-lg transition-all">
                  <div className="flex items-start justify-between gap-4">
                    <div>
                      <p className="text-sm font-bold text-slate-500">{vaga.company_name} • {vaga.cidade}</p>
                      <h3 className="font-heading text-xl font-extrabold mt-1">{vaga.cargo}</h3>
                    </div>
                    <span className={`shrink-0 text-xs font-extrabold rounded-full px-3 py-1 flex items-center gap-1 ${badge}`}><BadgeCheck size={13} /> {s.total}/6</span>
                  </div>
                  <p className="text-slate-600 mt-4 line-clamp-2">{vaga.descricao}</p>
                  <div className="mt-5 flex items-center justify-between gap-3">
                    <strong className="text-slate-950">Salário: {vaga.salario || "não informado"}</strong>
                    <span className="text-sm font-extrabold text-blue-700">{s.label}</span>
                  </div>
                </Link>
              );
            })}
          </div>
        )}
      </section>
    </main>
  );
}
